import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import styled, { useTheme } from 'styled-components';

import useGetSearchParams from 'hooks/useGetSearchParams';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';

const InfluencerCategorys = ['S등급/메가', 'A등급/매크로', 'B등급/마이크로'];

const convertRatingToKorean = (rating: string): string => {
	switch (rating) {
		case 's':
			return 'S등급/메가';
		case 'a':
			return 'A등급/매크로';
		case 'b':
			return 'B등급/마이크로';
		default:
			return 'S등급/메가';
	}
};

const convertRating = (rating: string) => {
	switch (rating) {
		case 'S등급/메가':
			return 's';
		case 'A등급/매크로':
			return 'a';
		case 'B등급/마이크로':
			return 'b';
		default:
			return 's';
	}
};

const convertRatingToLevel = (rating: string, platform: 'ig' | 'yt'): string => {
	const fllowerKr = platform === 'ig' ? '팔로워' : '구독자';
	const megaKr = platform === 'ig' ? '30만 이상' : '20만 이상';
	const macroKr = platform === 'ig' ? '5~30만' : '5만 이상';
	const microKr = platform === 'ig' ? '5만 이하' : '1만 이상';
	switch (rating) {
		case 's':
			return `메가(${fllowerKr} ${megaKr})`;
		case 'a':
			return `매크로(${fllowerKr} ${macroKr})`;
		case 'b':
			return `마이크로(${fllowerKr} ${microKr})`;
		default:
			return `메가(${fllowerKr} ${megaKr})`;
	}
};

const SearchFilterColumn = () => {
	const theme = useTheme();
	const [searchParams, setSearchParams] = useSearchParams();
	const [platform, rating] = useGetSearchParams(['platform', 'rating']);

	const selectedRating = useMemo(() => {
		return convertRatingToKorean(rating);
	}, [rating]);

	const setSelectedChips = (rating: string) => {
		const selectedRating = convertRating(rating);
		searchParams.set('rating', selectedRating);
		setSearchParams(searchParams);
	};

	const followerTxt = platform === 'ig' ? '팔로워' : '구독자';

	useEffect(() => {
		if (rating == null) {
			searchParams.set('rating', 's');
			setSearchParams(searchParams, { replace: true });
		}
	}, [platform]);

	return (
		<StickyColumn options={{ scrollbars: { visibility: 'hidden' } }}>
			<Grid flex="between" direction="column" _height="100%" _width="200px">
				<Grid _padding="24px 16px">
					<Text typo="span">브랜드 등급별 추천 매칭</Text>
					<Grid flex="start">
						<CoreRadioChips
							group_type="multi_line"
							style_type="default"
							_width="max-content"
							chips_list={InfluencerCategorys}
							selected_chips={selectedRating}
							setSelectedChips={setSelectedChips}
							_disabled={[]}
						/>
					</Grid>
				</Grid>
				<Grid
					_padding="24px 16px"
					flex="start"
					align_items="start"
					_gap="8px"
					direction="column"
				>
					<Grid flex="start" align_items="center" _margin="2px 0">
						<CoreSvg _size="14px" _fill={theme.colors.gray.gray30} _margin="0  4px 0 0">
							<Icon.HelpOutline />
						</CoreSvg>

						<Text typo="span_bold" color={theme.colors.gray.gray30}>
							크리에이터 레벨
						</Text>
					</Grid>

					<Text typo="span_bold" color={theme.colors.gray.gray80_text}>
						메가: {followerTxt} {platform === 'ig' ? '30만 이상' : '20만 이상'}
					</Text>
					<Text typo="span_bold" color={theme.colors.gray.gray80_text}>
						매크로: {followerTxt} {platform === 'ig' ? '5~30만' : '5만 이상'}
					</Text>
					<Text typo="span_bold" color={theme.colors.gray.gray80_text}>
						마이크로: {followerTxt} {platform === 'ig' ? '5만 이하' : '1만 이상'}
					</Text>
				</Grid>
			</Grid>
		</StickyColumn>
	);
};

const { Grid, Text } = style;

const StickyColumn = styled(OverlayScrollbarsComponent)`
	height: calc(100vh - 141px);
	background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
	border-right: 1px solid ${({ theme }) => theme.colors.gray.gray15};

	flex: 0 0 200px;
	display: flex;
	flex-direction: column;
	justify-items: stretch;
`;

export default SearchFilterColumn;
